import React, { Fragment, useContext, useState } from "react";
import { InputContext } from "../shared/contexts/InputContext";
import RadioInput from "../components/general/inputs/RadioInput";
import DateInput from "../components/general/inputs/DateInput";
import NumericInput from "../components/general/inputs/NumericInput";
import Stepper from "../components/yourdetails/Stepper";
import PartTimeServiceInput from "../components/general/inputs/PartTimeServiceInput";
import { addYearsToDate } from "../shared/utils/dateUtils";
import EligabilityVideo from "../assests/videos/3_Inputs_Step_1.mp4";
import CareerVideo from "../assests/videos/4_Inputs_Step_2.mp4";
import PartTimeVideo from "../assests/videos/5_Inputs_Step_3.mp4";
import PreferencesVideo from "../assests/videos/6_Inputs_Step_4.mp4";
import VideoButton from "../components/general/VideoButton";

const YourDetailsPage = () => {
  const { inputs, updateInput } = useContext(InputContext);

  let EligabilityStep = "";
  let CareerStep = "";
  let PartTimeServiceStep = "";
  let PreferencesStep = "";

  const [errors, setErrors] = useState({});
  //console.log('Errors: ', errors)
  //console.log('member info', inputs.memberInfo)
  const [msgs, setMsgs] = useState({});
  //console.log('Msgs: ', msgs)

  EligabilityStep = (
    <div>
      <VideoButton videoTitle="Your Eligibility" video={EligabilityVideo} />
      <h3>Check Your Eligibility</h3>
      <RadioInput
        desc_text={
          "Are you already in receipt of some or all of your pension benefits in the Northern Ireland Civil Service Pension Scheme?"
        }
        options={[
          { label: "Yes", value: "yes" },
          { label: "No", value: "no" },
        ]}
        inputObject={"memberInfo"}
        fieldName={"inReceipt"}
        inputs={inputs}
        updateInput={updateInput}
        errors={errors}
      />
      <RadioInput
        desc_text={
          "Are you currently an active member of the Northern Ireland Civil Service Pension Scheme?"
        }
        options={[
          { label: "Yes", value: "yes" },
          { label: "No", value: "no" },
        ]}
        inputObject={"memberInfo"}
        fieldName={"hasLeft"}
        inputs={inputs}
        updateInput={updateInput}
        errors={errors}
      />
      <RadioInput
        desc_text={"Are you affected by the McCloud Remedy?"}
        options={[
          { label: "Yes", value: "yes" },
          { label: "No", value: "no" },
        ]}
        inputObject={"memberInfo"}
        fieldName={"isMcCloudEligible"}
        inputs={inputs}
        updateInput={updateInput}
        errors={errors}
      />
    </div>
  );

  CareerStep = (
    <div>
      <VideoButton videoTitle="Your Career" video={CareerVideo} />
      <h3>Tell Us About Your Career</h3>
      <p>
        Please note that this section and the following section requires details
        of previous and current salaries and part time working. If you’re not
        sure of exact details, you may find it useful to refer to previous
        employment or pension communications such as payslips and/or Annual
        Benefit Statements. If these are not available, you may wish to use
        approximations. Please note: the more accurate the information that is
        put into the calculator, the better the calculator can estimate your
        projected benefits.
      </p>
      <RadioInput
        desc_text={
          "Which Northern Ireland Civil Service Pension Scheme were you a member of prior to 1st April 2015?"
        }
        options={[
          { label: "Classic", value: "classic" },
          { label: "Classic Plus", value: "classic_plus" },
          { label: "Premium", value: "premium" },
        ]}
        inputObject={"memberInfo"}
        fieldName={"legacyScheme"}
        inputs={inputs}
        updateInput={updateInput}
        errors={errors}
      />
      <DateInput
        desc_text={"What is your date of birth?"}
        inputObject={"memberInfo"}
        fieldName={"dateOfBirth"}
        inputs={inputs}
        updateInput={updateInput}
        errors={errors}
        msgs={msgs}
      />
      <DateInput
        desc_text={"What date did you join the Northern Ireland Civil Service Pension Scheme?"}
        extra_text={
          "For members that joined Premium from the Classic scheme in the 2002 exercise, they should enter their date of joining the scheme as the date they joined Classic. The illustrator will apply the associated 0.92 service adjustment as required."
        }
        inputObject={"memberInfo"}
        fieldName={"dateJoinedScheme"}
        inputs={inputs}
        updateInput={updateInput}
        errors={errors}
        msgs={msgs}
      />
      <DateInput
        desc_text={
          "When do you want to partially retire? The illustrator will assume the first of the month selected."
        }
        extra_text={
          "This illustrator restricts partial retirements to within the next 5 years. Uncertainty in the illustrations increases the further into the future the selected partial retirement date falls."
        }
        inputObject={"memberInfo"}
        help_text={
          "Partial retirement date selected must be within the next 5 years. Choosing a date further into the future increases the uncertainty in the illustrations provided."
        }
        fieldName={"dateOfPartRet"}
        inputs={inputs}
        //we want to update both full retirement date and partial retirement date when this is triggered
        updateInput={(inputObject, fieldName, formattedDate) => {
          if (formattedDate.length === 10) {
            //i.e., if we have a valid date
            const newFRDate = addYearsToDate(new Date(formattedDate), 1)
              .toISOString()
              .split("T")[0];

            // Use the passed-in updateInput prop to update both fields
            updateInput(inputObject, "dateOfFullRet", newFRDate);
          }

          updateInput(inputObject, fieldName, formattedDate);
        }}
        errors={errors}
        msgs={msgs}
      />
      <NumericInput
        desc_text={
          "What are your current gross full-time equivalent annual pensionable earnings?"
        }
        inputObject={"memberInfo"}
        fieldName={"salaryCurrent"}
        inputs={inputs}
        updateInput={updateInput}
        errors={errors}
        currencySymbol={true}
        msgs={msgs}
      />
      <NumericInput
        desc_text={
          "What were your gross full-time equivalent pensionable earnings on 1 April 2015? (If unknown, please enter 0)"
        }
        inputObject={"memberInfo"}
        fieldName={"salaryApril2015"}
        inputs={inputs}
        updateInput={updateInput}
        errors={errors}
        currencySymbol={true}
        msgs={msgs}
      />
    </div>
  );

  PartTimeServiceStep = (
    <div>
      <VideoButton videoTitle="Your Part-Time Service" video={PartTimeVideo} />
      <h3>Tell Us About Your Part-Time Service</h3>
      <PartTimeServiceInput
        min_date={new Date(inputs.memberInfo.dateJoinedScheme)}
        max_date={new Date(inputs.memberInfo.dateOfPartRet)}
      />
    </div>
  );

  PreferencesStep = (
    <div>
      <VideoButton videoTitle="Your Plan" video={PreferencesVideo} />
      <h3>Tell Us About Your Plan</h3>
      <NumericInput
        desc_text={
          "After taking partial retirement, what will your new full-time equivalent salary be, as a percentage of your salary immediately before partial retirement? This value should be between 1% and 100%, with 100% representing no change to your full-time equivalent salary."
        } //Re-worded this section to reflect percentage and not monetary salary amount
        extra_text={
          "Note, post-partial retirement pensionable earnings must not exceed 80% of pre-partial retirement pensionable earnings. Please ensure that your expected post-partial retirement full-time equivalent earnings multiplied by the post-partial retirement part-time percentage below is less than 80% of the pre-partial retirement equivalent, otherwise your benefits may be overstated."
        }
        inputObject={"memberInfo"}
        help_text={
          <>
            <p>
              Please enter the percentage of your full-time equivalent pay you expect to earn after partial retirement. 
              This should reflect your full-time equivalent salary, ignoring any changes in working hours.
            </p>
            <p>
              If your role is expected to stay the same, please enter 100%.
            </p>
            <p>
              Your full-time equivalent salary after partial retirement is estimated based on this input, your current salary,
              and the assumed annual salary increases.
            </p>
          </>
        }//Added text to the help box from design spec
        fieldName={"salaryPostPartRet"}
        inputs={inputs}
        updateInput={updateInput}
        errors={errors}
        min={0}
        max={100}
        percentSymbol={true}
        msgs={msgs}
      />
      <NumericInput
        desc_text={
          "What will your part-time percentage be after you take partial retirement? This value should be between 1% and 100%. It should reflect the percentage of full-time equivalent hours that you will work after partial retirement. For example, if you will work 29.6 hours (4 days) per week when the full-time equivalent is 37 hours (5 days) per week, your part-time proportion would be 80%, in which case you should write ‘80’ in the percentage field."
        }
        extra_text={
          "This information will be used when projecting accrued pension amounts after partial retirement and is assumed to remain unchanged over the period between partial retirement and full retirement."
        }
        inputObject={"memberInfo"}
        fieldName={"partTimeServicePostPartRet"}
        help_text={
          "This value cannot be above 100%. If a value above 100% is entered, it will default to 100%."
        }
        inputs={inputs}
        updateInput={updateInput}
        errors={errors}
        min={0}
        max={100}
        percentSymbol={true}
        msgs={msgs}
      />
    </div>
  );

  const steps = [
    EligabilityStep,
    CareerStep,
    PartTimeServiceStep,
    PreferencesStep,
  ];

  return (
    <Fragment>
      <h2>Your Details</h2>
      <p>Please complete the following form.</p>
      <Stepper
        steps={steps}
        setErrors={setErrors}
        setMsgs={setMsgs}
        memberInfo={inputs.memberInfo}
      />
    </Fragment>
  );
};

export default YourDetailsPage;
